/**
 * Helper: Bloomreach
 * ------------------------------------------------------------------------------
 * - Universal component to interact with Bloomreach / Exponea SDK.
 * - Bloomreach events MUST be routed through this helper for GDPR reasons.
 * - Use EventBus events (e.g. `bloomreach:trackEvent` to submit events).
 * - Use Exponea Browser Extension to help track events.
 * - https://documentation.bloomreach.com/engagement/docs/integration
 * - https://documentation.bloomreach.com/engagement/docs/web-tracking
 * - https://documentation.bloomreach.com/engagement/docs/tag-manager
 *
 * @namespace Bloomreach
 */
import Cookies from 'js-cookie'

import { waitFor } from '~/helpers/dom'
import { storageNamespace } from '~/helpers/storage-namespaces'

/**
 * As of 10/24, Bloomreach doesn't support GA4. Their SDK will keep looking
 * for the Universal Analytics object but fail to find it as `window.gtag`
 * supersedes it.
 * - This doesn't impact Bloomreach reporting, but will clog up Sentry.
 * - Create a dummy GA object to prevent this.
 * - https://mint-velvet.sentry.io/issues/5703067837
 * - https://mint-velvet.atlassian.net/browse/MT-3231
 */
window.ga = window.ga || function() {}

/**
 * Create a new Bloomreach object.
 */
export default () => {

  /**
   * Global state.
   */
  const state = {
    allowEvents: false,
  }

  /**
   * Initialise component.
   * Flow:
   * - Wait for exponea variable to be defined
   * - Initiate EventBus Listeners
   * - Initiate SDK
   */
  function init() {
    waitFor('exponea')
      .then(() => {
        setEventBusListeners()
        setDefaultState()
      })
      .catch(() => {
        // Error handled within `waitFor`.
      })
  }

  /**
   * Set the default state of the helper.
   */
  function setDefaultState() {
    state.allowEvents = Boolean(mint.customer.canBeTracked)

    /**
     * User does not consent - destroy.
     */
    if (!state.allowEvents) {
      return
    }

    /**
     * User consents, but SDK is already initialised.
     */
    if (exponea.isInitialized) {
      return
    }

    initialiseBloomreach()
  }

  /**
   * Set Event Bus listeners.
   */
  function setEventBusListeners() {

    /**
     * The user has modified their consent status.
     */
    mint.EventBus.on('customer:stateUpdated', () => {
      setDefaultState()
    })

    /**
     * The Bloomreach API is ready - as announced by this component.
     */
    mint.EventBus.on('bloomreach:ready', () => {
      checkCustomerForms()
    })

    /**
     * A component has requested to submit an event to Bloomreach.
     * - Accepts an event and a payload, as per documentation.
     * @example
     *  mint.EventBus.emit('bloomreach:trackEvent', {
     *    event: 'name_of_event'
     *    data: <Object>
     * })
     */
    mint.EventBus.on('bloomreach:trackEvent', (payload) => {
      trackEvent(payload)
    })

    /**
     * A component has requested to identify the customer.
     * @example
     *  mint.EventBus.emit('bloomreach:identify', {
     *    email: '<String>'
     * })
     */
    mint.EventBus.on('bloomreach:identify', (payload) => {
      identifyCustomer(payload)
    })

    /**
     * Cart has updated.
     * - Event is emitted from Pinia / Vue 3.
     */
    mint.EventBus.on('mint:cart:updated', (payload) => {
      if (payload.request?.preventTracking) {
        return
      }

      handleUpdatedCart(payload)
    })
  }

  /**
   * Initialise Bloomreach event tracking.
   */
  function initialiseBloomreach() {
    const customer = {}
    const cartToken = Cookies.get('cart')

    if (!exponea) {
      mint.Log('error', 'Error initialising Bloomreach', new Error('Exponea SDK not found'))

      return
    }

    if (mint.customer?.email) {
      customer.email_id = mint.customer.email
      customer.shopify_id = mint.customer.id
    }

    if (cartToken) {
      customer.cart_id = cartToken
    }

    exponea.start({
      customer,
      track: {
        default_properties: {
          domain: mint.shop.permanentDomain,
        },
      },
    })

    mint.EventBus.dispatch('bloomreach:ready')
  }

  /**
   * Check if a customer has recently submitted a login/register form.
   * - components/customer/form-listener.js listens to form submits and
   *   stores in sessionStorage
   * - On page load, this function checks for that login attempt and decides
   *   if the user has successfully logged in
   * - If yes: Submit email to Bloomreach
   */
  function checkCustomerForms() {
    const loginAttempt = JSON.parse(
      sessionStorage.getItem(storageNamespace.customerFormSubmit),
    ) || false

    /**
     * Customer is:
     * - Has not recently attempted to login.
     * - Already logged in (existing session)
     */
    if (
      !loginAttempt &&
      (mint.customer?.hasAccount && mint.customer?.loggedIn)
    ) {
      return
    }

    /**
     * - Customer is not logged in
     * - Customer has not attempted to login.
     */
    if (!loginAttempt) {
      return
    }

    /**
     * There *was* a log in attempt, but:
     * - The customer is not logged in (attempt failed)
     * - The customer has tried with two different emails
     * - The customer does not allow event tracking
     */
    if (
      !mint.customer.loggedIn ||
      (loginAttempt.email !== mint.customer?.email) ||
      !state.allowEvents
    ) {
      removeStoredLoginAttempt()

      return
    }

    /**
     * The customer is successfully logged in.
     */

    let event = loginAttempt.type

    /**
     * Format to match Bloomreach event.
     */
    if (event === 'login') {
      event = 'log_in'
    }

    mint.EventBus.dispatch('bloomreach:identify', {
      email_id: loginAttempt.email,
    })

    mint.EventBus.dispatch('bloomreach:trackEvent', {
      event,
      data: {
        email: loginAttempt.email,
      },
    })
  }

  /**
   * Track cart updates.
   * @param {Object} cartData - response object from `cart:updated` event.
   */
  function handleUpdatedCart(cartData) {
    const cartAction = cartData.request.event.split(':')[1]
    const { meta } = cartData.request

    let data = {}

    /**
     * Build core data.
     * - Bloomreach requires numbers to not be in cents.
     *   e.g. '£32' should be '32' and not '3200'
     */
    data.currency = cartData.currency
    data.price = Number(cartData.total_price / 100)
    data.total_quantity = Number(cartData.item_count)
    data.total_price = Number(cartData.total_price / 100)

    /**
     * Build products.
     */
    data.product_list = cartData.items.map((item) => {
      return {
        product_id: item.product_id,
        product_qty: Number(item.quantity),
        sku: item.sku,
      }
    })

    data.product_details = cartData.items.map((item) => {
      return {
        image_url: item.image,
        product_id: Number(item.product_id),
        item_qty: Number(item.quantity),
        sku: item.sku,
        price: Number(item.price / 100),
        title: item.title,
        variant_id: Number(item.variant_id),
      }
    })

    /**
     * Build data around specific actions.
     */
    /**
     * User has added to the cart.
     */
    if (cartAction === 'added') {
      data.action = 'add'

      if (cartData.request) {
        const matchingLineItem = cartData.items.find((item) => {
          return Number(item.variant_id) === Number(cartData.request?.data?.id)
        })

        data = {
          ...data,
          price:  Number(matchingLineItem.price / 100),
          product_id:  Number(matchingLineItem.product_id),
          variant_id:  Number(cartData.request.data?.id),
          quantity:  Number(cartData.request.data?.quantity),
        }

        /**
         * If metadata is present in the `cartData.request` object, extract the
         * `section` value (usually the section name in the trackingParameters),
         * then update the tracking data object with the new `data_source` property.
         */
        if (cartData.request.meta) {
          const data_source = cartData.request.meta.source?.section || ''
          const flexi_fashion = cartData.request.meta?.flexiFashionName || ''

          data = {
            ...data,
            data_source,
            flexi_fashion,
          }
        }
      }
    }

    /**
     * User has removed a cart item.
     */
    if (cartAction === 'removed') {
      data.action = 'remove'

      if (cartData.request) {
        const request = {
          price: Number(cartData.request.data.price / 100),
          variant_id: Number(cartData.request.data.variant_id),
          product_id: Number(cartData.request.data.product_id),
        }

        data = {
          ...data,
          ...request,
        }
      }
    }

    /**
     * User has updated an existing cart item.
     */
    if (
      cartAction === 'changed' &&
      meta?.eventType === 'change_quantity'
    ) {
      data.action = 'quantity change'

      if (meta.id) {
        const matchingLineItem = cartData.items.find((item) => {
          return Number(item.variant_id) === Number(meta.id)
        })

        if (matchingLineItem) {
          const modified = {
            price: Number(matchingLineItem.price / 100),
            product_id: Number(matchingLineItem.product_id),
            quantity: Number(matchingLineItem.quantity),
            variant_id: Number(matchingLineItem.variant_id),
          }

          const request = {
            ...data,
            ...modified,
          }

          data = {
            ...data,
            ...request,
          }
        }
      }
    }

    const trackedEvent = {
      event: 'cart_update_web',
      data,
    }

    mint.EventBus.dispatch('bloomreach:trackEvent', trackedEvent)
  }

  /**
   * Emit an event to Bloomreach via the SDK.
   * @param {Object} payload - Oject containing user data.
   * @param {String} payload.event - Name of event to track.
   * @param {Object} payload.data - Data to submit to Bloomreach.
   */
  function trackEvent({ event, data = {} }) {
    if (!event || !state.allowEvents) {
      return
    }

    exponea.track(event, data)
  }

  /**
   * Emit an Identify event to Bloomreach.
   * - Identify requires two events, with an optional third.
   * - 1) exponea.identify: Hard identify of the customer in the BR database.
   * - 2) exponea.update: Custom attribute mapped to the customer file.
   * - 3) exponea.track: Optional event to confirm identification.
   * - https://documentation.bloomreach.com/engagement/docs/tracking#identify
   * @param {Object} payload - Oject containing user data.
   */
  function identifyCustomer(payload) {
    if (!payload || !state.allowEvents) {
      return
    }

    /**
     * Identify should have the params:
     * `email_id: <email>`
     * - This is to correctly map within the Bloomreach database.
     * - This will be set in the payload.
     */
    exponea.identify(payload)

    /**
     * Bloomreach state that after identify(), the below should run:
     * - Unlike `exponea.identify`, `exponea.update` and track: identify
     *   should use: `email: <email>`.
     */
    exponea.update({
      email: payload.email_id,
    })

    /**
     * Remove any previous login attempt.
     */
    removeStoredLoginAttempt()

    mint.EventBus.dispatch('bloomreach:trackEvent', {
      event: 'identify',
      data: {
        email: payload.email_id,
      },
    })
  }

  /**
   * Remove the previous login attempt from local storage.
   */
  function removeStoredLoginAttempt() {
    sessionStorage.removeItem(storageNamespace.customerFormSubmit)
  }

  /**
   * Expose public interface.
   */
  return Object.freeze({
    init,
  })
}
